// DocumentSearchInput — the header `<input>` (CTSK-493 §5.4; design doc
// §3.1, §6.5, §6.6). Controlled text; the container owns the dropdown
// state machine and passes down what ARIA needs to describe it.
//
// ARIA: the combobox role sits on the input itself (ARIA 1.2 pattern,
// same as the project's UserTypeahead) rather than on a wrapper with an
// inner searchbox as the design doc sketched — one role, one focusable,
// and axe-core's current combobox rules are written for 1.2. Documented
// as a §6.6 deviation in the ship report.

import type { KeyboardEvent, RefObject } from 'react';

export interface DocumentSearchInputProps {
  value: string;
  onChange: (next: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  inputRef: RefObject<HTMLInputElement>;
  /** id of the listbox (`aria-controls`). */
  listboxId: string;
  expanded: boolean;
  /** id of the keyboard-focused option, or undefined. */
  activeDescendant: string | undefined;
  loading: boolean;
  placeholder?: string;
}

export const DOCUMENT_SEARCH_PLACEHOLDER = 'Search documents...';

export function DocumentSearchInput({
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  inputRef,
  listboxId,
  expanded,
  activeDescendant,
  loading,
  placeholder = DOCUMENT_SEARCH_PLACEHOLDER,
}: DocumentSearchInputProps) {
  return (
    <div className="doc-search__field">
      <input
        ref={inputRef}
        type="search"
        className="doc-search__input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        autoComplete="off"
        spellCheck={false}
        role="combobox"
        aria-label="Search documents"
        aria-autocomplete="list"
        aria-haspopup="listbox"
        aria-expanded={expanded}
        aria-controls={listboxId}
        aria-activedescendant={expanded ? activeDescendant : undefined}
        aria-busy={loading || undefined}
        aria-keyshortcuts="/"
        data-testid="document-search-input"
      />
      {loading && (
        <span className="doc-search__spinner" aria-hidden="true" data-testid="document-search-spinner" />
      )}
    </div>
  );
}
